import type {
  AdminLeadDetail,
  AdminLeadGuestConversionDuplicateReview,
  AdminLeadGuestConversionRequest,
  IdempotencyKey,
} from "@whitehaven/contracts";
import { ContentState, Dialog, StatusPill } from "@whitehaven/ui";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  applyConfirmedAdminLeadGuestConversion,
  canPrepareAdminLeadGuestConversion,
  duplicateReviewDecisionFor,
  isAdminLeadGuestConversionFailureOutcome,
} from "./admin-lead-guest-conversion-model";
import type { AdminLeadGuestConversionMutationGateway } from "./lead-guest-conversion-gateway";

interface ConversionFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
}

interface AdminLeadGuestConversionPanelProps {
  readonly detail: AdminLeadDetail;
  readonly duplicateReview: AdminLeadGuestConversionDuplicateReview | null;
  readonly mutationGateway: AdminLeadGuestConversionMutationGateway | null;
  readonly detailRefreshing?: boolean;
  readonly externalMutationPending?: boolean;
  readonly onConfirmed: (detail: AdminLeadDetail) => void;
  readonly onPendingChange?: (pending: boolean) => void;
  readonly onRefreshRequired: () => void;
}

const conversionDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

function createConversionIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

export function AdminLeadGuestConversionPanel({
  detail,
  duplicateReview,
  mutationGateway,
  detailRefreshing = false,
  externalMutationPending = false,
  onConfirmed,
  onPendingChange,
  onRefreshRequired,
}: AdminLeadGuestConversionPanelProps) {
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<ConversionFeedback | null>(null);
  const mutationSequence = useRef(0);
  const activeMutation = useRef<AbortController | null>(null);
  const feedbackRef = useRef<HTMLParagraphElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const latestDetail = useRef(detail);
  latestDetail.current = detail;
  const latestReview = useRef(duplicateReview);
  latestReview.current = duplicateReview;

  const conversion = detail.guestConversion;
  const ready =
    mutationGateway !== null &&
    canPrepareAdminLeadGuestConversion(detail, duplicateReview);
  const controlsDisabled =
    !ready ||
    pending ||
    detailRefreshing ||
    externalMutationPending ||
    feedback?.refreshRequired === true;
  const reviewIdentity = duplicateReview?.candidateSetRevision ?? null;

  useEffect(() => {
    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-guest-conversion",
      () => {
        mutationSequence.current += 1;
        activeMutation.current?.abort();
        activeMutation.current = null;
        setPending(false);
        onPendingChange?.(false);
        setDialogOpen(false);
        setReviewConfirmed(false);
        setFeedback(null);
      },
    );

    return () => {
      unregisterCleaner();
      mutationSequence.current += 1;
      activeMutation.current?.abort();
      activeMutation.current = null;
      onPendingChange?.(false);
    };
  }, [onPendingChange]);

  useEffect(() => {
    if (activeMutation.current) return;
    setDialogOpen(false);
    setReviewConfirmed(false);
    setFeedback(null);
  }, [
    detail.summary.leadId,
    detail.guestConversionPreflightRevision,
    reviewIdentity,
  ]);

  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);

  const requireRefresh = (message: string) => {
    setFeedback({
      tone: "critical",
      message,
      refreshRequired: true,
    });
  };

  const confirmConversion = async () => {
    if (
      !mutationGateway ||
      !reviewConfirmed ||
      controlsDisabled ||
      activeMutation.current ||
      !duplicateReview ||
      !canPrepareAdminLeadGuestConversion(detail, duplicateReview) ||
      !detail.guestConversionPreflightRevision
    ) {
      return;
    }

    const idempotencyKey = createConversionIdempotencyKey();
    if (!idempotencyKey) {
      setDialogOpen(false);
      requireRefresh(
        "The conversion could not be prepared safely. Refresh the lead before trying again.",
      );
      return;
    }

    const request: AdminLeadGuestConversionRequest = {
      leadId: detail.summary.leadId,
      expectedUpdatedAt: detail.summary.updatedAt,
      conversionPreflightRevision: detail.guestConversionPreflightRevision,
      expectedDuplicateCandidateSetRevision:
        duplicateReview.candidateSetRevision,
      expectedDuplicateCandidateCount:
        duplicateReview.summary.candidateContactCount,
      duplicateReviewDecision: duplicateReviewDecisionFor(
        duplicateReview.summary.candidateContactCount,
      ),
    };
    const requestId = mutationSequence.current + 1;
    mutationSequence.current = requestId;
    const controller = new AbortController();
    activeMutation.current = controller;
    const protectedStateGeneration = captureProtectedStateGeneration();
    setPending(true);
    onPendingChange?.(true);
    setFeedback(null);

    try {
      const outcome = await mutationGateway(request, {
        signal: controller.signal,
        idempotencyKey,
      });

      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        return;
      }

      const currentDetail = latestDetail.current;
      const currentReview = latestReview.current;
      if (
        currentReview === null ||
        currentDetail.summary.leadId !== request.leadId ||
        currentDetail.summary.updatedAt !== request.expectedUpdatedAt ||
        currentDetail.guestConversionPreflightRevision !==
          request.conversionPreflightRevision ||
        currentReview.candidateSetRevision !==
          request.expectedDuplicateCandidateSetRevision
      ) {
        setDialogOpen(false);
        requireRefresh(
          "The lead or duplicate review changed while conversion was pending. Refresh before trying again.",
        );
        return;
      }

      if (isAdminLeadGuestConversionFailureOutcome(outcome)) {
        setDialogOpen(false);
        requireRefresh(
          outcome.kind === "conflict"
            ? "The lead, contact, or possible matches changed elsewhere. Refresh before converting."
            : "The guest conversion was not confirmed. Refresh before trying again.",
        );
        return;
      }

      const updatedDetail = applyConfirmedAdminLeadGuestConversion(
        currentDetail,
        currentReview,
        request,
        outcome,
      );
      if (!updatedDetail) {
        setDialogOpen(false);
        requireRefresh(
          "The conversion response could not be confirmed. Refresh the lead.",
        );
        return;
      }

      onConfirmed(updatedDetail);
      setDialogOpen(false);
      setReviewConfirmed(false);
      setFeedback({
        tone: "positive",
        message:
          "Guest conversion confirmed. Portal access has not been invited and no appointment was created.",
        refreshRequired: false,
      });
    } catch {
      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        return;
      }
      setDialogOpen(false);
      requireRefresh(
        "The guest conversion could not be confirmed. Refresh before trying again.",
      );
    } finally {
      if (
        mutationSequence.current === requestId &&
        isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        activeMutation.current = null;
        setPending(false);
        onPendingChange?.(false);
      }
    }
  };

  const inactive =
    detail.summary.leadRecordStatus !== "ACTIVE" ||
    detail.summary.contactRecordStatus !== "ACTIVE";

  return (
    <section
      className="crm-lead-guest-conversion crm-lead-engagement"
      aria-labelledby="crm-lead-guest-conversion-title"
      aria-describedby="crm-lead-guest-conversion-scope"
      aria-busy={pending ? "true" : undefined}
    >
      <div className="crm-lead-engagement__heading">
        <div>
          <h3 id="crm-lead-guest-conversion-title">Guest conversion</h3>
          <p id="crm-lead-guest-conversion-scope">
            Create or reuse the guest identity for this lead&apos;s current
            contact after a complete duplicate review.
          </p>
        </div>
        <StatusPill
          tone={conversion ? "positive" : inactive ? "warning" : "neutral"}
        >
          {conversion ? "Converted" : inactive ? "Read-only" : "Not converted"}
        </StatusPill>
      </div>

      {conversion && (
        <ContentState
          kind="success"
          headingLevel={4}
          title="Guest identity confirmed."
        >
          <dl className="crm-lead-guest-conversion__facts">
            <div>
              <dt>Guest reference</dt>
              <dd>
                <code>{conversion.guestId}</code>
              </dd>
            </div>
            <div>
              <dt>Guest identity</dt>
              <dd>
                {conversion.guestDisposition === "CREATED"
                  ? "Created for this contact"
                  : "Existing identity reused"}
              </dd>
            </div>
            <div>
              <dt>Converted</dt>
              <dd>
                <time dateTime={conversion.convertedAt}>
                  {conversionDateFormatter.format(
                    new Date(conversion.convertedAt),
                  )}
                </time>
              </dd>
            </div>
          </dl>
          <p>
            This did not send a login invitation, create an account session,
            schedule an appointment, merge contacts, or change pipeline stage.
          </p>
        </ContentState>
      )}

      {!conversion && inactive && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Inactive lead or contact cannot be converted."
        >
          <p>
            The retained CRM record remains visible, but conversion requires
            both the lead and its current contact to be active.
          </p>
        </ContentState>
      )}

      {!conversion && !inactive && mutationGateway === null && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Guest conversion is unavailable."
        >
          <p>
            The protected conversion service has not been connected. No sample
            or browser-only conversion is used in production.
          </p>
        </ContentState>
      )}

      {!conversion &&
        !inactive &&
        mutationGateway !== null &&
        !ready &&
        duplicateReview === null && (
          <ContentState
            kind="warning"
            headingLevel={4}
            title="Complete the duplicate review first."
          >
            <p>
              Load every possible matching contact above. Conversion stays
              disabled while that review is loading, incomplete, stale, or
              unavailable.
            </p>
          </ContentState>
        )}

      {!conversion &&
        !inactive &&
        mutationGateway !== null &&
        duplicateReview !== null &&
        !ready && (
          <ContentState
            kind="warning"
            headingLevel={4}
            title="The conversion snapshot is not current."
          >
            <p>
              Refresh the lead and complete duplicate review again before
              converting.
            </p>
          </ContentState>
        )}

      {!conversion && ready && duplicateReview && (
        <div className="crm-lead-guest-conversion__controls">
          {duplicateReview.summary.candidateContactCount > 0 ? (
            <ContentState
              kind="warning"
              headingLevel={4}
              title={`${duplicateReview.summary.candidateContactCount} possible matching ${
                duplicateReview.summary.candidateContactCount === 1
                  ? "contact requires"
                  : "contacts require"
              } an explicit decision.`}
            >
              <p>
                Conversion will keep this lead&apos;s current contact distinct.
                It will not merge or modify any possible match.
              </p>
            </ContentState>
          ) : (
            <p className="crm-lead-guest-conversion__review-note">
              The complete current duplicate review found no possible matching
              contacts.
            </p>
          )}

          <label className="crm-lead-guest-conversion__confirmation">
            <input
              type="checkbox"
              checked={reviewConfirmed}
              disabled={controlsDisabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setReviewConfirmed(event.currentTarget.checked);
                setFeedback(null);
              }}
            />
            <span>
              {duplicateReview.summary.candidateContactCount > 0
                ? "I reviewed all possible matches and confirm this contact should remain distinct."
                : "I reviewed this lead and confirm its current contact should become a guest."}
            </span>
          </label>

          <button
            type="button"
            className="crm-lead-detail__button"
            disabled={controlsDisabled || !reviewConfirmed}
            onClick={() => setDialogOpen(true)}
          >
            Review guest conversion
          </button>
        </div>
      )}

      {feedback && (
        <p
          ref={feedbackRef}
          className={`crm-lead-guest-conversion__feedback crm-lead-guest-conversion__feedback--${feedback.tone}`}
          role={feedback.tone === "critical" ? "alert" : "status"}
          tabIndex={-1}
        >
          {feedback.message}
        </p>
      )}

      {feedback?.refreshRequired && (
        <button
          type="button"
          className="crm-lead-detail__button crm-lead-detail__button--secondary"
          disabled={pending || detailRefreshing || externalMutationPending}
          onClick={onRefreshRequired}
        >
          Refresh lead and review
        </button>
      )}

      {dialogOpen && (
        <Dialog
          id="crm-lead-guest-conversion-dialog"
          open
          title="Confirm guest conversion"
          description="This is a terminal conversion record for this lead."
          initialFocusRef={cancelRef}
          closeOnEscape={!pending}
          closeOnBackdrop={false}
          onRequestClose={() => {
            if (!pending) setDialogOpen(false);
          }}
          actions={
            <>
              <button
                ref={cancelRef}
                type="button"
                className="crm-lead-detail__button crm-lead-detail__button--secondary"
                disabled={pending}
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crm-lead-detail__button"
                disabled={pending}
                onClick={() => void confirmConversion()}
              >
                {pending ? "Converting lead" : "Convert lead to guest"}
              </button>
            </>
          }
        >
          <ul className="crm-lead-guest-conversion__boundary-list">
            <li>The guest identity will be created or safely reused.</li>
            <li>No possible matching contact will be merged or changed.</li>
            <li>No login invitation or guest membership will be created.</li>
            <li>No appointment or message will be sent.</li>
            <li>The lead&apos;s pipeline stage will stay unchanged.</li>
          </ul>
        </Dialog>
      )}
    </section>
  );
}
